import React from 'react';
import get from 'lodash/get';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import zip from 'lodash/zip';
import isFinite from 'lodash/isFinite';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { TEACHER_AUTHORED } from 'chameleon-src/components/assignments/constants';
import { learnosityReportByUsers } from 'legacy-src/utility/progressHelpers';
import { resourceTypes } from 'chameleon/legacy/src/components/TeacherOrganize/utils/constants';
import { CASFields } from 'chameleon/legacy/src/components/assignment/Assignment/components/AssignmentTable/ColumnCAS';

export const noDataPlaceholder = <span className="mb-0">—</span>;

export const sortAssessmentTitle = () =>
  React.useCallback((a, b, field) => {
    const getTitle = (val) => get(val, field, '');
    const titleA = getTitle(a.original);
    const titleB = getTitle(b.original);

    if (titleA === titleB) return 0;

    const splitByNumbers = (str) => {
      const val = String(str).split(/(\d+)/gi);
      // if name starts with number than empty string should be removed from 0 position
      if (/^\d/.test(str)) {
        return val.slice(1);
      }
      return val;
    };
    const isNumber = (val) => !Number.isNaN(+val);

    const list = zip(splitByNumbers(titleA), splitByNumbers(titleB));
    const compare = (wordA, wordB) => {
      if (isNumber(wordA) && isNumber(wordB)) {
        const numA = +wordA;
        const numB = +wordB;
        return numA === numB ? 0 : numA > numB ? 1 : -1;
      }
      if (isNumber(wordA) && !isNumber(wordB)) {
        return -1; // number goes before string
      }
      if (!isNumber(wordA) && isNumber(wordB)) {
        return 1; // number goes before string
      }

      return wordA.localeCompare(wordB);
    };

    // eslint-disable-next-line no-unused-vars
    for (const [wordA, wordB] of list) {
      if (wordA == null) {
        return -1;
      }
      if (wordB == null) {
        return 1;
      }

      const res = compare(wordA, wordB);
      if (res !== 0) {
        return res;
      }
    }

    return 0;
  }, []);

export const sortAssessmentResults = () =>
  React.useCallback((rowA, rowB) => {
    const assignmentA = get(rowA, 'original.assignments[0]', null);
    const assignmentB = get(rowB, 'original.assignments[0]', null);

    // This ensures unassigned assessment(s) stays below
    // in desc order and at the top in asc order.
    if (assignmentA === null && assignmentB !== null) {
      return -1;
    }

    if (assignmentA !== null && assignmentB === null) {
      return 1;
    }

    if (assignmentA === null && assignmentB === null) {
      return 0;
    }

    if (assignmentA.hasStudents && !assignmentB.hasStudents) {
      return 1;
    }

    if (!assignmentA.hasStudents && assignmentB.hasStudents) {
      return -1;
    }

    if (
      isFinite(assignmentA.scorePercentage) &&
      !isFinite(assignmentB.scorePercentage)
    ) {
      return 1;
    }

    if (assignmentA.scorePercentage > assignmentB.scorePercentage) {
      return 1;
    }

    return -1;
  }, []);

export const sortAssessmentStatus = () =>
  React.useCallback((rowA, rowB) => {
    const assessmentA = rowA.original;
    const assessmentB = rowB.original;

    // PDFs are not supposed to be mixed with other types
    if (
      assessmentA.type === resourceTypes.pdf &&
      assessmentB.type === resourceTypes.pdf
    ) {
      const getPDFSortingCriteria = (pdf) => {
        const {
          visibility_to_students: visibilityToStudent,
          shared_with: sharedWith,
        } = pdf;
        if (visibilityToStudent === 'AlwaysShow') {
          return 1;
        }
        if (!sharedWith.length) {
          return 2;
        }
        return 3;
      };
      const pdfASortingCriteria = getPDFSortingCriteria(assessmentA);
      const pdfBSortingCriteria = getPDFSortingCriteria(assessmentB);
      if (pdfASortingCriteria > pdfBSortingCriteria) {
        return 1;
      }
      if (pdfASortingCriteria < pdfBSortingCriteria) {
        return -1;
      }
      return 0;
    }

    const assignmentA = get(assessmentA, 'assignments[0]', null);
    const assignmentB = get(assessmentB, 'assignments[0]', null);
    // This ensures unassigned assessment(s) stays below
    // in desc order and at the top in asc order.
    if (assignmentA === null && assignmentB !== null) {
      return -1;
    }

    if (assignmentA !== null && assignmentB === null) {
      return 1;
    }

    if (assignmentA === null && assignmentB === null) {
      return 0;
    }
    // prepare assessment data
    const getAssignmentSortingData = (row) => {
      const assessment = row.original || {};
      const { is_spp: isSPP, assignments } = assessment || {};
      const assignment = assignments[0];
      const {
        report,
        students_who_submitted_spp: submittedStudents,
        studentCount,
        progress,
      } = assignment || {};

      const {
        complete,
        not_started: notStarted,
        ready_to_score: readyToScore,
        scoring,
        scored,
        started,
        student_scored: studentScored,
        submitted,
        teacher_and_student_scored: teacherStudentScored,
      } = progress || {};

      if (isSPP) {
        /**
         * We need to find the students who have learnosity reports and those
         * APC knows submitted, the intersection between these two is
         * the source of truth behind an SPP submission count.
         */
        const hasScoredResult = report && !isEmpty(report);
        const reportByUsers =
          hasScoredResult && learnosityReportByUsers([report]);
        const reportUsers = !isEmpty(reportByUsers)
          ? Object.keys(reportByUsers)
          : [];
        const apcLRNSubmittedIntersection = submittedStudents.filter((value) =>
          reportUsers.includes(String(value)),
        );
        const submittedSum = apcLRNSubmittedIntersection.length;

        let total = submittedSum / studentCount || 0;

        return {
          totalStudents: studentCount,
          total,
          isSPP: true,
        };
      }

      const totalStudents =
        complete +
        submitted +
        notStarted +
        scoring +
        studentScored +
        started +
        readyToScore +
        teacherStudentScored +
        scored;
      let total =
        (complete + submitted + scored + teacherStudentScored + studentScored) /
          totalStudents || 0;
      const Scored = readyToScore + scoring;
      return {
        totalStudents,
        total,
        Scored,
        isSPP: false,
      };
    };

    // prepare video data
    const getVideoSortingData = (row) => {
      const videoTotalStudents =
        row.original.assignments[0].progress.total_students;
      const watched =
        row.original.assignments[0].progress.watched /
          row.original.assignments[0].progress.total_students || 0;
      return {
        videoTotalStudents,
        watched,
      };
    };

    // return weights
    const getRowWeight = (row, sortingResults) => {
      const { videoTotalStudents, watched } = getVideoSortingData(row);

      let weight = 0;
      if (sortingResults.totalStudents || videoTotalStudents) {
        // % submitted
        if (
          (watched || sortingResults.total) >= 0 &&
          (watched || sortingResults.total) < 1
        ) {
          weight = 1 + (watched || sortingResults.total);
        }
        // to score, SPPs do not have a to score state since they are autoscored FRQs
        if (sortingResults.Scored && !sortingResults.isSPP) {
          weight =
            2 + (sortingResults.Scored / 1000 + 1 / sortingResults.Scored);
        }
        // completed videos
        if (
          watched === 1 ||
          row.original.assignments[0].status === 'completed'
        ) {
          weight = 4;
        }
        // completed assignments
        if (
          sortingResults.total === 1 ||
          row.original.assignments[0].actions.includes('reassign')
        ) {
          weight = 5;
        }
      } else {
        const { status } = row.original.assignments[0] || {};
        /**
         * When comparing classes without students, ensure locked
         * assignments appear above open ones when sorting in ascending
         * order.
         */
        if (status === 'locked') {
          weight = 0.1;
        } else if (['opened', 'open'].includes(status)) {
          weight = 0.2;
        }
      }
      return weight;
    };

    const assignmentSortingResultsA = getAssignmentSortingData(rowA);
    const assignmentSortingResultsB = getAssignmentSortingData(rowB);

    const weightA = getRowWeight(rowA, assignmentSortingResultsA);
    const weightB = getRowWeight(rowB, assignmentSortingResultsB);

    if (weightA > weightB) {
      return 1;
    }

    if (weightB > weightA) {
      return -1;
    }

    return 0;
  }, []);

export const sortAssessmentByDate = (dateSortType) =>
  React.useCallback((a, b, id, desc) => {
    const getDateSort = (dateSortType) => {
      if (dateSortType === 'date_created') return 'date_created';
      if (dateSortType === 'date_updated') return 'date_updated';
      return 'unknown';
    };

    /**
     * These first 3 conditions keep our teacher-authored rows at the bottom
     */
    if (
      a.original.type !== TEACHER_AUTHORED &&
      b.original.type === TEACHER_AUTHORED
    ) {
      return desc ? -1 : 1;
    }

    if (
      a.original.type === TEACHER_AUTHORED &&
      b.original.type !== TEACHER_AUTHORED
    ) {
      return desc ? 1 : -1;
    }

    if (
      a.original.type !== TEACHER_AUTHORED &&
      b.original.type !== TEACHER_AUTHORED
    ) {
      return 0;
    }

    const sortByType = getDateSort(dateSortType);

    const dateTypeA = Number(new Date(a.original[sortByType]));
    const dateTypeB = Number(new Date(b.original[sortByType]));

    return dateTypeA - dateTypeB;
  }, []);

export const isStudentSubmissionLate = ({
  submitted,
  customSetting,
  dueAt,
} = {}) => {
  if (!submitted) return false;
  if (!customSetting && !dueAt) return false;
  // A null CAS due date means that the teacher has deliberately chosen to allow the student to submit at any time
  if (customSetting && isNull(customSetting[CASFields.DueAt])) return false;
  if (customSetting && isUndefined(customSetting[CASFields.DueAt]) && !dueAt)
    return false;
  // Otherwise we inherit CAS due date value or assignment due date
  const dueAtOfInterest = get(customSetting, CASFields.DueAt, dueAt) || dueAt;

  // Submission time could be delayed because of scheduling and processing time.
  // So we're adding a buffer time of 1 min to due date so the submission will not be considered late.
  const dueAtWithBuffer = dayjs(dueAtOfInterest).utc(false).add(1, 'minute');
  return dayjs.utc(submitted).isAfter(dueAtWithBuffer);
};

export const transformAssociatedResourcesList = (associatedResources) =>
  associatedResources.reduce((accum, obj) => {
    accum[obj['resource_type'] || obj['resourceType']] =
      obj['resource_url'] || obj['resourceUrl'];
    return accum;
  }, {});
